import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoCheckmark, IoOpenOutline } from 'react-icons/io5';
import { cancelPendingAction, confirmPendingAction, getConnectLink, getPendingAction } from '../atlasApi';
import AppLogo from './AppLogo';
import { useCardDecided } from './cardDecisions';

// "Connect Google Drive": Atlas needs an app the user hasn't connected. The sign-in
// opens in a new tab; Atlas waits on this card and carries on once it's connected.
// The card checks the connection (confirm = "check now") while the user is signing
// in and whenever they come back to this tab, so it settles even if Atlas already
// stopped waiting. A card can bring its own wording (title, notice, button): the
// "Reconnect Gmail" card an email tool shows for a Gmail still on the old connection.
// Every click asks Atlas for a fresh sign-in link: Composio's links expire within
// minutes, so the one made with the card is often dead by the time it's clicked.
const CHECK_EVERY_MS = 4000;
const CHECK_FOR_MS = 15 * 60 * 1000;

export default function AppConnectCard({ approval }) {
  const actionId = approval?.actionId;
  const [status, setStatus] = useState(approval?.status || 'pending');
  const [preview, setPreview] = useState(approval?.preview || {});
  const [signingIn, setSigningIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const onDecided = useCardDecided();
  const statusRef = useRef(status);
  statusRef.current = status;

  const settle = useCallback((next) => {
    if (!next || next === statusRef.current) return;
    setStatus(next);
    if (next !== 'pending') onDecided?.({ actionId, kind: 'app_connect', status: next, app: preview.app });
  }, [actionId, onDecided, preview.app]);

  useEffect(() => {
    let cancelled = false;
    if (!actionId) return undefined;
    getPendingAction({ actionId })
      .then((data) => {
        if (cancelled) return;
        if (data?.preview && typeof data.preview === 'object') setPreview(data.preview);
        // Already settled before this card mounted (e.g. a reload): show it, don't re-report.
        if (data?.status && data.status !== 'pending') setStatus(data.status);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [actionId]);

  const check = useCallback(async () => {
    if (!actionId || statusRef.current !== 'pending') return;
    try {
      const res = await confirmPendingAction({ actionId });
      settle(res?.status);
    } catch {
      // A failed check is "not yet"; the next one tries again.
    }
  }, [actionId, settle]);

  // While signing in: check every few seconds, for a while.
  useEffect(() => {
    if (!signingIn || status !== 'pending') return undefined;
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started > CHECK_FOR_MS) {
        clearInterval(timer);
        return;
      }
      check();
    }, CHECK_EVERY_MS);
    return () => clearInterval(timer);
  }, [signingIn, status, check]);

  // Coming back to this tab from the sign-in tab: check right away.
  useEffect(() => {
    if (status !== 'pending') return undefined;
    const onBack = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', onBack);
    document.addEventListener('visibilitychange', onBack);
    return () => {
      window.removeEventListener('focus', onBack);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [status, check]);

  // The tab opens inside the click (so it isn't blocked as a pop-up), then goes to the new link.
  const openSignIn = useCallback(async (event) => {
    event.preventDefault();
    if (!actionId) return;
    setError('');
    const tab = window.open('', '_blank');
    try {
      const res = await getConnectLink({ actionId });
      const fresh = res?.connect_url;
      if (!fresh) throw new Error('No sign-in link came back.');
      setPreview((p) => ({ ...p, connect_url: fresh }));
      if (tab) {
        tab.opener = null;
        tab.location.href = fresh;
      } else {
        window.location.assign(fresh);
      }
      setSigningIn(true);
    } catch (e) {
      if (tab) tab.close();
      if (e?.status === 409) {
        check();
        return;
      }
      setError(e?.message || 'Could not start the sign-in. Try again in a moment.');
    }
  }, [actionId, check]);

  const notNow = useCallback(async () => {
    if (!actionId || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await cancelPendingAction({ actionId });
      settle(res?.status || 'cancelled');
    } catch (e) {
      setError(e?.message || 'Could not update this card.');
    } finally {
      setBusy(false);
    }
  }, [actionId, busy, settle]);

  const app = preview.app || 'the app';
  const reconnect = preview.button === 'Reconnect';
  const button = preview.button || 'Connect';
  const pending = status === 'pending';
  const title = status === 'done'
    ? `${app} ${reconnect ? 'reconnected' : 'connected'}`
    : status === 'cancelled'
      ? `${app} not ${reconnect ? 'reconnected' : 'connected'}`
      : preview.title || `Connect ${app}`;
  const detail = status === 'done'
    ? 'Atlas can use it now.'
    : status === 'cancelled'
      ? `You can ${reconnect ? 'reconnect' : 'connect'} it any time in Settings → Connectors.`
      : signingIn
        ? `Finish signing in to ${app} in the new tab. Atlas carries on here once it's connected.`
        : preview.notice || `Atlas needs your ${app} account to continue.`;

  return (
    <div className={`atlas-connect-card atlas-connect-card-${status}`} data-testid="app-connect">
      <div className="atlas-connect-card-logo">
        <AppLogo toolkit={preview.toolkit} logo={preview.logo} size={22} />
        {status === 'done' && (
          <span className="atlas-connect-card-badge" aria-hidden="true"><IoCheckmark size={10} /></span>
        )}
      </div>
      <div className="atlas-connect-card-text">
        <div className="atlas-connect-card-title">{title}</div>
        <div className="atlas-connect-card-detail">
          {pending && signingIn && <span className="atlas-connect-card-spinner" aria-hidden="true" />}
          {detail}
        </div>
        {error && <div className="atlas-connect-card-error">{error}</div>}
      </div>
      {pending && (
        <div className="atlas-connect-card-actions">
          {signingIn ? (
            <button type="button" className="atlas-connect-card-primary" onClick={check} disabled={busy}>
              I&apos;ve connected
            </button>
          ) : (
            <a
              className="atlas-connect-card-primary"
              href={preview.connect_url || undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openSignIn}
              aria-label={`${button} ${app}`}
            >
              {button}
              <IoOpenOutline size={13} />
            </a>
          )}
          {signingIn && (
            <a
              className="atlas-connect-card-quiet"
              href={preview.connect_url || undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openSignIn}
            >
              Reopen sign-in
            </a>
          )}
          <button type="button" className="atlas-connect-card-quiet" onClick={notNow} disabled={busy}>
            Not now
          </button>
        </div>
      )}
    </div>
  );
}
